const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'packages/patapp_web/src/actions/appointment.ts';
import { AppointmentType, StructuredAppointment } from '@cistec/common_web/defs/kisim';
import {
    KeyValuePair,
    KISIM_TIME_FORMAT,
    KISIM_TIME_LOWER_LIMIT,
    KISIM_TIME_OPEN,
} from '@cistec/common_web/defs/generalDefs';
import moment from 'moment';
import { createPatapiCall, HttpMethod } from 'App/utils/fetchUtils';
import { NotLinkedPatient, Slot, TreatmentReasonListItem } from 'App/defs/types';
import { v4 as uuidv4 } from 'uuid';
import { IKisApiActionsMap, kisapiActionTypes } from '@cistec/common_web/defs/actionTypes';
import { timeRangeToLookForFutureAppointments } from 'App/utils/appointment';

export const BOOK_APPOINTMENT = kisapiActionTypes('PATAPP_BOOK_APPOINTMENT');
export const CANCEL_APPOINTMENT = kisapiActionTypes('PATAPP_CANCEL_APPOINTMENT');
export const LOAD_STRUCTURED_APPOINTMENT = kisapiActionTypes('PATAPP_LOAD_STRUCTURED_APPOINTMENT');
export const LOAD_STRUCTURED_APPOINTMENTS = kisapiActionTypes('PATAPP_LOAD_STRUCTURED_APPOINTMENTS');
export const LOAD_PAST_STRUCTURED_APPOINTMENTS = kisapiActionTypes('PATAPP_LOAD_PAST_STRUCTURED_APPOINTMENTS');
export const RESCHEDULE_APPOINTMENT = kisapiActionTypes('PATAPP_RESCHEDULE_APPOINTMENT');

export function actionLoadStructuredAppointment(terminNr: string, allowedAppointmentTypes: AppointmentType[]) {
    const endpoint = `appointments/id/${terminNr}`;
    const meta = {
        terminNr,
        allowedAppointmentTypes,
    };
    return createPatapiCall(LOAD_STRUCTURED_APPOINTMENT, endpoint, HttpMethod.GET, meta);
}

const genericStructuredAppointmentGetter = (
    type: IKisApiActionsMap,
    allowedAppointmentTypes: AppointmentType[],
    mindat: string = KISIM_TIME_LOWER_LIMIT,
    maxdat: string = KISIM_TIME_OPEN
) => {
    const params = {
        mindat,
        maxdat,
    };
    const queryString = new URLSearchParams(params).toString();
    const endpoint = `appointments?${queryString}`;
    const meta = {
        mindat,
        maxdat,
        allowedAppointmentTypes,
    };
    return createPatapiCall(type, endpoint, HttpMethod.GET, meta);
};

export function actionLoadFutureAppointments(allowedAppointmentTypes: AppointmentType[]) {
    const { mindat } = timeRangeToLookForFutureAppointments();
    return genericStructuredAppointmentGetter(LOAD_STRUCTURED_APPOINTMENTS, allowedAppointmentTypes, mindat);
}

export function actionLoadPastAppointments(allowedAppointmentTypes: AppointmentType[]) {
    return genericStructuredAppointmentGetter(
        LOAD_PAST_STRUCTURED_APPOINTMENTS,
        allowedAppointmentTypes,
        undefined,
        moment().format(KISIM_TIME_FORMAT)
    );
}

export function actionCancelAppointment(appointment: StructuredAppointment) {
    const endpoint = `appointments/id/${appointment.TERMNR}/cancel`;
    const meta = { appointment };
    return createPatapiCall(CANCEL_APPOINTMENT, endpoint, HttpMethod.POST, meta);
}

export type PatientData = {
    PATNR: string;
    EMAIL: string;
    NAME: string;
    VORNAME: string;
    SEX: string;
    STRASSE: string;
    PLZ: string;
    ORT: string;
    LAND: string;
    TELEFON: string;
    TELEFON_G: string;
    TELEFON_M: string;
    DATE_OF_BIRTH: string;
    SOZVERSNR: string;
};

export function actionBookAppointment(
    treatmentReason: TreatmentReasonListItem,
    slot: Slot,
    patient: PatientData,
    notLinkedPatient: NotLinkedPatient,
    formData: KeyValuePair,
    history: any
) {
    const appointmentParams: any = {
        Patient: patient,
        Bemerkung: formData.comment,
        KIND: treatmentReason.fkey,
    };

    if (treatmentReason.duration) {
        appointmentParams.STARTDAT = moment(slot.startDate).format(KISIM_TIME_FORMAT);
        appointmentParams.STOPDAT = moment(slot.startDate)
            .add(treatmentReason.duration, 'minutes')
            .format(KISIM_TIME_FORMAT);
    }
    const meta = {
        history,
        patient,
        notLinkedPatient,
        formData,
        id: uuidv4(),
    };

    const endpoint = `appointments`;
    const body = JSON.stringify({
        Slot: {
            Id: slot.id,
        },
        Appointment: appointmentParams,
        Type: 'BOOKING',
    });
    return createPatapiCall(BOOK_APPOINTMENT, endpoint, HttpMethod.POST, meta, body);
}

export const actionRescheduleAppointment = (appointment: StructuredAppointment, slot: Slot, history: any) => {
    const meta = {
        history,
        slot,
        appointment,
    };

    const endpoint = `appointments/id/${appointment.TERMNR}/reschedule`;
    const body = JSON.stringify({
        Slot: {
            Id: slot.id,
        },
        Appointment: {
            Bemerkung: appointment.BEMERKUNGEN,
        },
        Type: 'BOOKING',
    });
    return createPatapiCall(RESCHEDULE_APPOINTMENT, endpoint, HttpMethod.POST, meta, body);
};
